import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CreditCard, Loader2, MoreVertical, Search, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import billingApi from '../../api/billingApi';
import { AdminBillingOverviewRow } from '../../api/types';
import AdminBillingPlansModal from './components/AdminBillingPlansModal';
import { useAdminContext } from './AdminContext';

const formatMoney = (cents: number, currency = 'usd') => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    maximumFractionDigits: 0,
}).format((cents || 0) / 100);

const formatDate = (value?: string | null) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
    });
};

const statusLabel = (value: string) => {
    const normalized = (value || '').replace(/_/g, ' ');
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const statusClass = (value: string) => {
    switch (value) {
        case 'active':
        case 'paid':
            return 'bg-emerald-50 text-emerald-700';
        case 'trial':
        case 'canceling':
            return 'bg-yellow-50 text-yellow-700';
        case 'past_due':
        case 'unpaid':
            return 'bg-red-50 text-red-700';
        default:
            return 'bg-gray-100 text-gray-600';
    }
};

const AdminBillingPage: React.FC = () => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const { setSelectedBusinessId } = useAdminContext();
    const [search, setSearch] = useState('');
    const [stripeFilter, setStripeFilter] = useState('all');
    const [qboFilter, setQboFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [openMenuId, setOpenMenuId] = useState<string | null>(null);
    const [selectedBillingBusiness, setSelectedBillingBusiness] = useState<AdminBillingOverviewRow | null>(null);

    const { data, isLoading, error, refetch } = useQuery({
        queryKey: ['adminBillingOverview'],
        queryFn: billingApi.getAdminOverview,
    });

    const rows = useMemo(() => data?.data.rows || [], [data?.data.rows]);
    const summary = data?.data.summary;

    const filteredRows = useMemo(() => {
        const needle = search.trim().toLowerCase();
        return rows.filter((row) => {
            const matchesSearch = !needle || [
                row.business_name,
                row.owner_email,
                row.business_id,
                row.plan_name,
            ].some((value) => String(value || '').toLowerCase().includes(needle));
            const matchesStripe = stripeFilter === 'all' || (stripeFilter === 'connected' ? row.stripe_linked : !row.stripe_linked);
            const matchesQbo = qboFilter === 'all' || (qboFilter === 'connected' ? row.qbo_linked : !row.qbo_linked);
            const matchesStatus = statusFilter === 'all' || row.billing_status === statusFilter;
            return matchesSearch && matchesStripe && matchesQbo && matchesStatus;
        });
    }, [qboFilter, rows, search, statusFilter, stripeFilter]);

    const clearFilters = () => {
        setSearch('');
        setStripeFilter('all');
        setQboFilter('all');
        setStatusFilter('all');
    };

    if (isLoading) {
        return (
            <div className="flex h-full items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
            </div>
        );
    }

    if (error) {
        return <div className="p-6 text-red-600">Failed to load billing overview.</div>;
    }

    return (
        <div className="flex h-full flex-col p-6">
            <div className="mb-6 flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">Billing</h2>
                <button
                    onClick={() => {
                        refetch();
                        queryClient.invalidateQueries({ queryKey: ['adminBillingOverview'] });
                    }}
                    className="rounded-lg bg-gray-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-gray-800"
                >
                    Refresh
                </button>
            </div>

            <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-4">
                <div className="rounded-lg border border-gray-300 bg-white p-5">
                    <p className="text-sm text-gray-600">Active Subscriptions</p>
                    <p className="mt-2 text-xl font-semibold text-gray-900">{summary?.active_subscriptions || 0}</p>
                </div>
                <div className="rounded-lg border border-gray-300 bg-white p-5">
                    <p className="text-sm text-gray-600">Monthly Revenue</p>
                    <p className="mt-2 text-xl font-semibold text-gray-900">{formatMoney(summary?.monthly_revenue_cents || 0)}</p>
                </div>
                <div className="rounded-lg border border-gray-300 bg-white p-5">
                    <p className="text-sm text-gray-600">Past Due</p>
                    <p className="mt-2 text-xl font-semibold text-gray-900">{summary?.past_due || 0}</p>
                </div>
                <div className="rounded-lg border border-gray-300 bg-white p-5">
                    <p className="text-sm text-gray-600">Trials</p>
                    <p className="mt-2 text-xl font-semibold text-gray-900">{summary?.trials || 0}</p>
                </div>
            </div>

            <div className="mb-5 flex flex-wrap items-center gap-3">
                <div className="relative min-w-[260px] flex-1">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <input
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder="Search business name, client name, email..."
                        className="h-10 w-full rounded-lg border border-gray-300 bg-white pl-10 pr-3 text-sm outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                    />
                </div>
                <select value={stripeFilter} onChange={(event) => setStripeFilter(event.target.value)} className="h-10 rounded-lg border border-gray-300 bg-white px-3 text-sm">
                    <option value="all">Stripe: All</option>
                    <option value="connected">Stripe: Connected</option>
                    <option value="not_connected">Stripe: Not connected</option>
                </select>
                <select value={qboFilter} onChange={(event) => setQboFilter(event.target.value)} className="h-10 rounded-lg border border-gray-300 bg-white px-3 text-sm">
                    <option value="all">QBO: All</option>
                    <option value="connected">QBO: Connected</option>
                    <option value="not_connected">QBO: Not connected</option>
                </select>
                <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} className="h-10 rounded-lg border border-gray-300 bg-white px-3 text-sm">
                    <option value="all">Status: All</option>
                    <option value="active">Active</option>
                    <option value="trial">Trial</option>
                    <option value="trial_ended">Trial ended</option>
                    <option value="canceling">Canceling</option>
                    <option value="past_due">Past due</option>
                    <option value="free">Free</option>
                </select>
                <button onClick={clearFilters} className="inline-flex h-10 items-center gap-2 rounded-lg px-3 text-sm text-gray-600 hover:bg-gray-100">
                    <X className="h-4 w-4" />
                    Clear
                </button>
            </div>

            <div className="min-h-0 flex-1 overflow-hidden rounded-xl border border-gray-300 bg-white">
                <div className="h-full overflow-auto">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="sticky top-0 bg-white text-left text-gray-600">
                            <tr>
                                <th className="px-5 py-4 font-medium">Business</th>
                                <th className="px-5 py-4 font-medium">Plan</th>
                                <th className="px-5 py-4 font-medium">Monthly Price</th>
                                <th className="px-5 py-4 font-medium">Billing Status</th>
                                <th className="px-5 py-4 font-medium">Payment Status</th>
                                <th className="px-5 py-4 font-medium">Renewal Date</th>
                                <th className="px-5 py-4 text-right font-medium">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                            {filteredRows.map((row) => (
                                <tr key={`${row.workspace_id}:${row.business_id}`} className="hover:bg-gray-50">
                                    <td className="px-5 py-4">
                                        <div className="font-medium text-gray-900">{row.business_name}</div>
                                        <div className="text-xs text-gray-500">{row.owner_email || row.business_id}</div>
                                    </td>
                                    <td className="px-5 py-4 text-gray-900">{row.plan_name}</td>
                                    <td className="px-5 py-4 text-gray-900">{row.monthly_price_cents ? formatMoney(row.monthly_price_cents, row.currency) : '—'}</td>
                                    <td className="px-5 py-4">
                                        <span className={`rounded px-2.5 py-1 text-xs font-medium ${statusClass(row.billing_status)}`}>{statusLabel(row.billing_status)}</span>
                                    </td>
                                    <td className="px-5 py-4">
                                        <span className={`rounded px-2.5 py-1 text-xs font-medium ${statusClass(row.payment_status)}`}>{statusLabel(row.payment_status)}</span>
                                    </td>
                                    <td className="px-5 py-4 text-gray-900">{formatDate(row.renewal_date)}</td>
                                    <td className="relative px-5 py-4 text-right">
                                        <button
                                            onClick={() => setOpenMenuId(openMenuId === row.workspace_id ? null : row.workspace_id)}
                                            className="rounded p-1 text-gray-500 hover:bg-gray-100"
                                        >
                                            <MoreVertical className="h-4 w-4" />
                                        </button>
                                        {openMenuId === row.workspace_id && (
                                            <div className="absolute right-5 top-10 z-20 w-36 rounded-lg border border-gray-200 bg-white py-1 text-left shadow-lg">
                                                <button
                                                    onClick={() => {
                                                        setSelectedBusinessId(row.business_id);
                                                        setOpenMenuId(null);
                                                        navigate('/admin');
                                                    }}
                                                    className="block w-full px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-50"
                                                >
                                                    View Business
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setSelectedBillingBusiness(row);
                                                        setOpenMenuId(null);
                                                    }}
                                                    className="block w-full px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-50"
                                                >
                                                    Change Plan
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setOpenMenuId(null);
                                                        alert('Invoice drilldown is not connected yet. Use Stripe dashboard for invoice-level detail for now.');
                                                    }}
                                                    className="block w-full px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-50"
                                                >
                                                    View Invoices
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setOpenMenuId(null);
                                                        alert('Retry payment is not connected yet. Use Stripe dashboard to retry the latest failed invoice.');
                                                    }}
                                                    className="block w-full px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-50"
                                                >
                                                    Retry Payment
                                                </button>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {filteredRows.length === 0 && (
                                <tr>
                                    <td colSpan={7} className="px-5 py-16 text-center text-gray-500">
                                        <CreditCard className="mx-auto mb-3 h-8 w-8 text-gray-300" />
                                        No billing records match your filters.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {selectedBillingBusiness && (
                <AdminBillingPlansModal
                    isOpen={Boolean(selectedBillingBusiness)}
                    workspaceId={selectedBillingBusiness.workspace_id}
                    businessName={selectedBillingBusiness.business_name}
                    currentPlanId={selectedBillingBusiness.plan_id}
                    currentPlanStatus={selectedBillingBusiness.plan_status}
                    onClose={() => {
                        setSelectedBillingBusiness(null);
                        queryClient.invalidateQueries({ queryKey: ['adminBillingOverview'] });
                    }}
                />
            )}
        </div>
    );
};

export default AdminBillingPage;
